import dayjs from 'dayjs'
import type { IDashboardFilter, IDateRangeKey, InteractiveReportKey } from '@query'
import type { ReportColumn } from '@utils/generateReport'

/** Period keys from GET /dashboard/filters/, plus FE-only `custom`. */
export type PeriodPreset = IDateRangeKey

/** Parity with the old comparison-period form, which allowed four columns. */
export const MAX_PERIOD_COLUMNS = 4

/** Fits a cross-year header like `25.4.2022 – 3.8.2026` plus the caret button. */
export const ACCOUNTING_VALUE_COLUMN_WIDTH_PX = 208
export const ACCOUNTING_VALUE_COLUMN_WIDTH = `${ACCOUNTING_VALUE_COLUMN_WIDTH_PX}px`

/** Mirrors backend MAX_ALLOWED_COLUMNS_WITH_COMPARISON in reports/tasks.py. */
export const MAX_COLUMNS_WITH_COMPARISON = 3

export type FiscalYear = { start_date: string; end_date: string }

export type FiscalYearContext = {
  /** Accounting periods ascending by start date. */
  years: FiscalYear[]
  /** Index of the active period in `years`, or -1 when unknown. */
  activeIndex: number
}

export const EMPTY_FISCAL_YEAR_CONTEXT: FiscalYearContext = { years: [], activeIndex: -1 }

/** A period column the user manages from the report header. */
export type PeriodColumn = {
  id: string
  preset: PeriodPreset
  /** Balance sheet as-of date (custom or stamped from filter date_to). */
  asOf?: string
  /** Range start stamped from the dashboard filter or custom dialog. */
  dateFrom?: string
  /** Range end / balance sheet as-of stamped from the dashboard filter or custom dialog. */
  dateTo?: string
  /** Overrides the generated header label and the request column name. */
  title?: string
}

type DateRange = { dateFrom: string; dateTo: string }

export type ResolvedPeriod = DateRange & { label: string }

const fmt = (date: dayjs.Dayjs) => date.format('YYYY-MM-DD')
const display = (value: string) => dayjs(value).format('D.M.YYYY')

/** Same-year windows drop the start year (`1.8. – 31.8.2026`); cross-year keeps both. */
const displayRange = (dateFrom: string, dateTo: string) => {
  const from = dayjs(dateFrom)
  const to = dayjs(dateTo)
  const start = from.year() === to.year() ? from.format('D.M.') : display(dateFrom)
  return `${start} – ${display(dateTo)}`
}

const currentMonthRange = (): DateRange => {
  const today = dayjs()
  return { dateFrom: fmt(today.startOf('month')), dateTo: fmt(today.endOf('month')) }
}

let columnIdSequence = 0
export const createColumnId = () => `period-column-${(columnIdSequence += 1)}`

/** Keep new column ids unique after restoring persisted columns. */
export const syncColumnIdSequence = (columns: PeriodColumn[]) => {
  columns.forEach(column => {
    const match = /^period-column-(\d+)$/.exec(column.id)
    if (!match) return
    columnIdSequence = Math.max(columnIdSequence, Number(match[1]))
  })
}

export const supportsMultipleColumns = (reportKey: InteractiveReportKey) =>
  reportKey === 'balance_sheet' || reportKey === 'income_statement'

/** Only the balance sheet is an as-of report; specs and everything else use a date range. */
export const usesAsOfPeriod = (reportKey: InteractiveReportKey) => reportKey === 'balance_sheet'

const columnFromRange = (
  reportKey: InteractiveReportKey,
  preset: PeriodPreset,
  range: DateRange,
  existingId?: string
): PeriodColumn => {
  const base = {
    id: existingId || createColumnId(),
    preset,
    dateFrom: range.dateFrom,
    dateTo: range.dateTo
  }
  return usesAsOfPeriod(reportKey) ? { ...base, asOf: range.dateTo } : base
}

/**
 * Stamp a column from a dashboard filter. Balance sheet keeps `asOf = date_to` for the
 * as-of dialog; every other report uses the full range as returned by the API.
 */
export const columnFromFilter = (
  filter: IDashboardFilter,
  reportKey: InteractiveReportKey,
  existingId?: string
): PeriodColumn =>
  columnFromRange(
    reportKey,
    filter.key,
    { dateFrom: filter.date_from, dateTo: filter.date_to },
    existingId
  )

export const createDefaultColumn = (
  selectedRange: IDashboardFilter,
  reportKey: InteractiveReportKey
): PeriodColumn => columnFromFilter(selectedRange, reportKey)

/** Prefer this-month from dashboard options; fall back to a custom current-month window. */
export const createInsertedColumn = (
  reportKey: InteractiveReportKey,
  options: IDashboardFilter[] = []
): PeriodColumn => {
  const monthThis = options.find(option => option.key === 'month-this')
  if (monthThis?.date_from && monthThis?.date_to) {
    return columnFromFilter(monthThis, reportKey)
  }

  return columnFromRange(reportKey, 'custom', currentMonthRange())
}

const stampedRange = (column: PeriodColumn): DateRange => {
  const fallback = currentMonthRange()
  return {
    dateFrom: column.dateFrom || fallback.dateFrom,
    dateTo: column.dateTo || column.asOf || fallback.dateTo
  }
}

export const resolveColumn = (
  reportKey: InteractiveReportKey,
  column: PeriodColumn
): ResolvedPeriod => {
  const range = stampedRange(column)
  const autoLabel = usesAsOfPeriod(reportKey)
    ? display(range.dateTo)
    : displayRange(range.dateFrom, range.dateTo)

  return { ...range, label: column.title?.trim() || autoLabel }
}

export const resolveColumns = (
  reportKey: InteractiveReportKey,
  columns: PeriodColumn[]
): ResolvedPeriod[] => columns.map(column => resolveColumn(reportKey, column))

/**
 * Window for the account drill-down beneath a report row.
 *
 * A balance sheet figure is cumulative from inception, so its drill has to list
 * every entry up to the as-of date. Filter-backed columns still carry the period
 * `dateFrom`, which would show only part of the movement behind the number.
 */
export const resolveDrillWindow = (
  reportKey: InteractiveReportKey,
  column: PeriodColumn,
  context: FiscalYearContext = EMPTY_FISCAL_YEAR_CONTEXT
): ResolvedPeriod => {
  const period = resolveColumn(reportKey, column)
  if (!usesAsOfPeriod(reportKey)) return period

  const inception = context.years[0]?.start_date
  if (!inception || inception >= period.dateFrom) return period

  return { ...period, dateFrom: inception }
}

export const migrateColumnForReport = (
  column: PeriodColumn,
  reportKey: InteractiveReportKey
): PeriodColumn => {
  const range = stampedRange(column)
  const dates = usesAsOfPeriod(reportKey)
    ? { asOf: range.dateTo, dateFrom: range.dateFrom, dateTo: range.dateTo }
    : { dateFrom: range.dateFrom, dateTo: range.dateTo }
  return { ...column, ...dates }
}

export const migrateColumnsForReport = (
  columns: PeriodColumn[],
  reportKey: InteractiveReportKey
): PeriodColumn[] => columns.map(column => migrateColumnForReport(column, reportKey))

export const buildReportColumns = ({
  reportKey,
  columns
}: {
  reportKey: InteractiveReportKey
  columns: PeriodColumn[]
  /** @deprecated Unused; dates are stamped on the column from dashboard filters. */
  context?: FiscalYearContext
}): ReportColumn[] => {
  const resolved = resolveColumns(reportKey, columns)

  if (usesAsOfPeriod(reportKey)) {
    return resolved.map(period => ({ name: period.label, date_at: period.dateTo }))
  }

  const ranges: ReportColumn[] = resolved.map(period => ({
    name: period.label,
    date_from: period.dateFrom,
    date_to: period.dateTo
  }))

  // Ledger, journal, VAT and EU sales only ever read the first column.
  return supportsMultipleColumns(reportKey) ? ranges : ranges.slice(0, 1)
}

export const buildLedgerDrillColumns = (period: DateRange): ReportColumn[] => [
  {
    date_from: period.dateFrom,
    date_to: period.dateTo
  }
]

/** Comparison needs at least two periods and is dropped by the backend beyond three. */
export const canShowComparison = (columnCount: number) =>
  columnCount >= 2 && columnCount <= MAX_COLUMNS_WITH_COMPARISON

/** Detect interleaved Change columns from the response shape: [P1, Change, P2, …]. */
export const hasChangeColumns = (periodColumnCount: number, labelCount: number) =>
  periodColumnCount > 1 && labelCount === periodColumnCount * 2 - 1

export const REPORT_LINE_ITEM_MIN_WIDTH_PX = 200
export const REPORT_ROW_GAP_PX = 8
export const REPORT_ROW_PAD_Y_PX = 6
/** Vertical padding for entry drill-down rows. */
export const ENTRY_ROW_PAD_Y_PX = 3
/** Hit target for the entry attachment paperclip button. */
export const ENTRY_ATTACHMENT_BUTTON_SIZE_PX = 28
/** Keeps opening-balance / empty-slot rows the same height as attachment rows. */
export const ENTRY_ROW_MIN_HEIGHT_PX = ENTRY_ATTACHMENT_BUTTON_SIZE_PX + ENTRY_ROW_PAD_Y_PX * 2
export const REPORT_ROW_HORIZONTAL_PADDING_PX = 32
export const REPORT_ROW_BASE_PAD_LEFT_PX = 16
export const REPORT_VALUE_COLUMN_WIDTH_PX = 128
export const REPORT_VALUE_CELL_PAD_X_PX = 8
export const ADD_COLUMN_SLOT_WIDTH_PX = 34

/** Background for ledger drill-down rows and the related period column. */
export const REPORT_DRILL_COLUMN_BG = '#f7fafd'

/** Ledger drill always reads the first (leftmost) period value column. */
export const DRILL_COLUMN_VALUE_INDEX = 0

const NARROW_ROW_PAD_BASE_PX = 12
const NARROW_ROW_PAD_STEP_PX = 6
const NARROW_ROW_PAD_MAX_PX = 30

/** Compress hierarchical row indent on narrow viewports while keeping depth readable. */
export const narrowRowPadLeftPx = (padLeft?: number): number => {
  if (padLeft == null || padLeft <= REPORT_ROW_BASE_PAD_LEFT_PX) return NARROW_ROW_PAD_BASE_PX
  const depth = Math.max(1, Math.round((padLeft - REPORT_ROW_BASE_PAD_LEFT_PX) / 12))
  return Math.min(NARROW_ROW_PAD_BASE_PX + depth * NARROW_ROW_PAD_STEP_PX, NARROW_ROW_PAD_MAX_PX)
}

/** Label-only indent so value columns stay aligned with the header row. */
export const rowLabelIndentPx = (totalPadLeft?: number): number => {
  if (totalPadLeft == null || totalPadLeft <= REPORT_ROW_BASE_PAD_LEFT_PX) return 0
  return totalPadLeft - REPORT_ROW_BASE_PAD_LEFT_PX
}

export const narrowRowLabelIndentPx = (totalPadLeft?: number): number => {
  const base = narrowRowPadLeftPx(REPORT_ROW_BASE_PAD_LEFT_PX)
  if (totalPadLeft == null || totalPadLeft <= REPORT_ROW_BASE_PAD_LEFT_PX) return 0
  return Math.max(0, narrowRowPadLeftPx(totalPadLeft) - base)
}

/** Levels 0–1 stay flush; hierarchical indent starts at level 2. */
export const hierarchyLabelIndentFrom = (level: number, step = 16): number => {
  if (level < 2) return REPORT_ROW_BASE_PAD_LEFT_PX
  return REPORT_ROW_BASE_PAD_LEFT_PX + level * step
}

const reportRowWidth = (...parts: number[]) =>
  REPORT_ROW_HORIZONTAL_PADDING_PX +
  parts.reduce((sum, part) => sum + part, 0) +
  REPORT_ROW_GAP_PX * Math.max(0, parts.length - 1)

const entryPanelTableMinWidthPx = () =>
  reportRowWidth(28, 92, REPORT_LINE_ITEM_MIN_WIDTH_PX, 110, 110, 128)

export const ledgerTableMinWidthPx = () =>
  Math.max(
    reportRowWidth(14, 46, REPORT_LINE_ITEM_MIN_WIDTH_PX, 120, 120, 120, 128),
    entryPanelTableMinWidthPx()
  )

export const accountingTableMinWidthPx = (valueColumnCount: number) =>
  Math.max(
    reportRowWidth(
      14,
      46,
      REPORT_LINE_ITEM_MIN_WIDTH_PX,
      ...Array.from({ length: valueColumnCount }, () => ACCOUNTING_VALUE_COLUMN_WIDTH_PX),
      ADD_COLUMN_SLOT_WIDTH_PX
    ),
    entryPanelTableMinWidthPx()
  )

export const journalTableMinWidthPx = () =>
  reportRowWidth(14, 46, REPORT_LINE_ITEM_MIN_WIDTH_PX, 120, 120, 28)

export const vatTableMinWidthPx = () =>
  reportRowWidth(14, REPORT_LINE_ITEM_MIN_WIDTH_PX, REPORT_VALUE_COLUMN_WIDTH_PX)

export const specificationsTableMinWidthPx = () =>
  Math.max(reportRowWidth(14, 46, REPORT_LINE_ITEM_MIN_WIDTH_PX), entryPanelTableMinWidthPx())

export const euSalesTableMinWidthPx = () =>
  reportRowWidth(140, 220, 180, 160, REPORT_VALUE_COLUMN_WIDTH_PX)
